import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { splitPartNumbers, normalisePN, findPartByNumber } from '../lib/partNumbers';
import { StatusBadge } from './Badges';
import { StockBadge } from './StockBadge';
import { formatPrice } from '../../server/inventory.js';
import SafeIcon from '../common/SafeIcon';
import FlagNLAModal from './FlagNLAModal';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { getDisplayPN } from './PartCard';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiHash, FiSettings, FiDatabase, FiFlag, FiFileText, FiTag, FiEye, FiArrowRight, FiArrowLeft, FiAlertTriangle } = FiIcons;

const altPNMap = [
  { key: 'b_pn',    label: 'Alternate 2' },
  { key: 'chlx_pn', label: 'Chromalox' },
  { key: 'f_pn',    label: 'Frigidaire' },
  { key: 'gem_pn',  label: 'Gemline' },
  { key: 'rs_pn',   label: 'Robertshaw' },
  { key: 'mm_pn',   label: 'Motors and Armatures' },
  { key: 'js_pn',   label: 'Johnstone' },
  { key: 'k_pn',    label: 'Kelvinator' },
  { key: 'l_pn',    label: 'L' },
  { key: 'm_pn',    label: 'Maytag' },
  { key: 'n_pn',    label: 'Norge' },
  { key: 'q_pn',    label: 'Q' }
];

const PartDetailPanel = ({ part, from = null, focusNumber = null, onClose }) => {
  const { nlaTickets, inventoryActive } = useAppContext();
  const { user } = useAuth();
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [currentPart, setCurrentPart] = useState(part);
  const [trail, setTrail] = useState(from ? [from] : []); // parts visited before this one
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [replacement, setReplacement] = useState({ loading: false, parts: [] });

  useEffect(() => {
    setCurrentPart(part);
    setTrail(from ? [from] : []);
    setSelectedBrand(null);
  }, [part, from]);

  const newerNumbers = splitPartNumbers(currentPart?.newer);
  const needsReplacement = currentPart?.status === 'Discontinued' || newerNumbers.length > 0;

  // Look up the catalog entries for this part's newer numbers so they can be opened directly.
  useEffect(() => {
    if (!currentPart?.id || !needsReplacement) {
      setReplacement({ loading: false, parts: [] });
      return undefined;
    }
    let cancelled = false;
    setReplacement({ loading: true, parts: [] });
    api('replacements', { id: currentPart.id })
      .then((res) => !cancelled && setReplacement({ loading: false, parts: res.parts || [] }))
      .catch(() => !cancelled && setReplacement({ loading: false, parts: [] }));
    return () => { cancelled = true; };
  }, [currentPart?.id, needsReplacement]);

  if (!currentPart) return null;

  const pendingTicket = nlaTickets.find(t => t.part_id === currentPart.id && t.status === 'Pending');
  const isDiscontinued = currentPart.status === 'Discontinued';

  const toggleBrand = (brand) => {
    if (selectedBrand?.key === brand.key) {
      setSelectedBrand(null);
    } else {
      setSelectedBrand({
        key: brand.key,
        name: brand.label,
        pn: currentPart[brand.key]
      });
    }
  };

  const renderSpecs = () => {
    const specs = [];
    for (let i = 1; i <= 12; i++) {
      const num = i.toString().padStart(2, '0');
      const name = currentPart[`spec${num}name`];
      const data = currentPart[`spec${num}data`];
      const hint = currentPart[`spec${num}hint`];
      
      if (name && name.trim()) {
        specs.push(
          <div key={num} className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-4 rounded-xl">
            <div className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1">{name}</div>
            <div className="text-sm text-slate-900 dark:text-white font-bold">{data || '---'}</div>
            {hint && <div className="text-[10px] text-slate-500 dark:text-slate-600 italic mt-1">{hint}</div>}
          </div>
        );
      }
    }
    return specs;
  };

  return (
    <div className="fixed inset-0 z-[100] flex justify-end">
      <div className="absolute inset-0 bg-slate-950/40 dark:bg-slate-950/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-200 dark:border-slate-800">
        
        {/* Header Section */}
        <div className="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-800 flex justify-between items-start bg-slate-50 dark:bg-slate-900">
          <div className="text-left flex-1 pr-4">
            {trail.length > 0 && (
              <button
                type="button"
                onClick={() => { setCurrentPart(trail[trail.length - 1]); setTrail((t) => t.slice(0, -1)); }}
                className="mb-3 inline-flex min-h-[44px] md:min-h-0 items-center gap-1.5 text-sm md:text-xs font-semibold text-blue-700 dark:text-blue-300 hover:underline"
              >
                <SafeIcon icon={FiArrowLeft} className="w-3.5 h-3.5" />
                Back to <span className="font-mono">{getDisplayPN(trail[trail.length - 1])}</span>
              </button>
            )}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <StatusBadge status={currentPart.status} />
              {currentPart.equip_type && (
                <span className="flex items-center gap-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 text-[10px] font-black px-2 py-0.5 rounded border border-blue-500/20 uppercase tracking-widest">
                  <SafeIcon icon={FiTag} className="w-3 h-3" />
                  {currentPart.equip_type}
                </span>
              )}
            </div>
            
            <div className="flex flex-wrap items-baseline gap-3 mb-1">
              <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white font-mono leading-none break-all">
                {getDisplayPN(currentPart)}
              </h2>
              {selectedBrand && (
                <div className="flex items-center gap-2 px-2.5 py-1 bg-blue-600 text-white rounded-full text-[10px] font-bold animate-in zoom-in duration-200 shadow-lg shadow-blue-900/20">
                  <SafeIcon icon={FiEye} className="w-3 h-3" />
                  <span>VIEWING AS: {selectedBrand.name} · {selectedBrand.pn}</span>
                  <button onClick={() => setSelectedBrand(null)} className="ml-1 hover:text-white/70">
                    <SafeIcon icon={FiX} className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Internal PN:</span>
              <span className="text-xs font-mono font-bold text-slate-600 dark:text-slate-400">{currentPart.part_numbr || '---'}</span>
            </div>
            <p className="text-slate-500 dark:text-slate-400 font-medium mt-3">{currentPart.descrip1}</p>
          </div>

          <div className="flex items-center gap-2">
            {user && !isDiscontinued && !pendingTicket && (
              <button 
                onClick={() => setShowFlagModal(true)}
                title="Flag as No Longer Available"
                className="flex h-11 w-11 items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-all border border-slate-200 dark:border-slate-700"
              >
                <SafeIcon icon={FiFlag} className="w-5 h-5" />
              </button>
            )}
            <button onClick={onClose} aria-label="Close details" className="flex h-11 w-11 items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 border border-transparent">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] space-y-6 sm:space-y-8 text-left">
          {/* Your shop's price and stock */}
          {inventoryActive && (
            <section className="border border-slate-300 dark:border-slate-700">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 dark:border-slate-800 dark:bg-slate-900">
                <h3 className="caption !text-slate-800 dark:!text-white">Your shop</h3>
              </div>
              {(currentPart.inventory || []).length === 0 ? (
                <p className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">Not in your shop's list under this part's numbers or its alternates.</p>
              ) : (
                <ul className="divide-y divide-slate-200 dark:divide-slate-800">
                  {currentPart.inventory.map((item, i) => (
                    <li key={i} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-sm font-semibold text-slate-900 dark:text-white">{item.pn}</span>
                          {item.match === 'alternate' && <span className="font-cond text-[11px] font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300">Alternate</span>}
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400">
                          {[item.description, item.sku && `SKU ${item.sku}`, item.bin && `Bin ${item.bin}`].filter(Boolean).join(' \u00b7 ')}
                        </div>
                        {item.cost !== undefined && item.cost !== null && (
                          <div className="text-xs text-slate-500">Your cost {formatPrice(item.cost)}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <StockBadge qty={item.qty} />
                        <span className="font-mono text-lg font-semibold text-slate-900 dark:text-white">{formatPrice(item.price) || '\u2014'}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {/* Replacement: shown first, because for a discontinued part it is the answer */}
          {(isDiscontinued || newerNumbers.length > 0) && (
            <section className={`border-l-[3px] p-4 ${isDiscontinued ? 'border-l-red-600 bg-red-50 dark:bg-red-500/10' : 'border-l-blue-600 bg-blue-50 dark:bg-blue-500/10'}`}>
              <h3 className="caption mb-3 flex items-center gap-2 !text-slate-800 dark:!text-white">
                <SafeIcon icon={isDiscontinued ? FiAlertTriangle : FiArrowRight} className={isDiscontinued ? 'text-red-600' : 'text-blue-600'} />
                {isDiscontinued ? 'Discontinued: use instead' : 'Newer part number available'}
              </h3>
              {newerNumbers.length === 0 ? (
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  No replacement number is recorded for this part. Check the alternate brands below{currentPart.advice ? ' and the advice notes' : ''}.
                </p>
              ) : (
                <ul className="space-y-2">
                  {newerNumbers.map((number) => {
                    const match = findPartByNumber(replacement.parts, number);
                    const focused = focusNumber && normalisePN(focusNumber) === normalisePN(number);
                    return (
                      <li key={number} className={`${focused ? 'ring-2 ring-blue-600 ' : ''}flex flex-wrap items-center justify-between gap-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-3 py-2`}>
                        <div className="min-w-0">
                          <div className="font-mono text-sm font-semibold text-slate-900 dark:text-white">{number}</div>
                          {match ? (
                            <div className="text-xs text-slate-600 dark:text-slate-400 truncate">
                              {match.descrip1} &middot; {match.status === 'Discontinued' ? 'also discontinued' : 'in catalog, active'}
                            </div>
                          ) : (
                            <div className="text-xs text-slate-500">{replacement.loading ? 'Checking catalog\u2026' : 'Not in this catalog'}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button type="button" onClick={() => navigator.clipboard?.writeText(number)} className="btn-secondary !px-4 !py-2 md:!py-1 text-xs">Copy</button>
                          {match && (
                            <button type="button" onClick={() => { setTrail((t) => [...t, currentPart]); setCurrentPart(match); }} className="btn-primary !px-4 !py-2 md:!py-1 text-xs">
                              Open <SafeIcon icon={FiArrowRight} />
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}

          {/* Alternate Brands */}
          <section>
            <h3 className="text-xs font-black text-emerald-600 dark:text-emerald-500 uppercase tracking-widest mb-4 flex items-center gap-2">
              <SafeIcon icon={FiDatabase} /> Alternate Brands
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {altPNMap.map(alt => {
                const isAvailable = !!currentPart[alt.key];
                const isSelected = selectedBrand?.key === alt.key;
                if (!isAvailable) return null;

                return (
                  <button
                    key={alt.key}
                    onClick={() => toggleBrand(alt)}
                    className={`text-left p-3 rounded-xl border transition-all duration-200 group relative ${
                      isSelected 
                        ? 'bg-blue-50 dark:bg-blue-500/10 border-blue-500 shadow-md ring-2 ring-blue-500/20' 
                        : 'bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800/50 hover:border-slate-400 dark:hover:border-slate-600'
                    } ${selectedBrand && !isSelected ? 'opacity-40 grayscale-[0.5]' : 'opacity-100'} `}
                  >
                    <div className={`text-[10px] font-black uppercase mb-1 transition-colors ${
                      isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400 dark:text-slate-500'
                    }`}>
                      {alt.label}
                    </div>
                    <div className={`text-xs font-mono font-bold transition-colors ${
                      isSelected ? 'text-blue-900 dark:text-blue-200' : 'text-slate-900 dark:text-white'
                    }`}>
                      {currentPart[alt.key]}
                    </div>
                    {isSelected && (
                      <div className="absolute top-2 right-2">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Identification Section */}
          <section>
            <h3 className="text-xs font-black text-blue-600 dark:text-blue-500 uppercase tracking-widest mb-4 flex items-center gap-2">
              <SafeIcon icon={FiHash} /> Identification
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <InfoBox label="OEM PN" value={currentPart.oem_pn} />
              <InfoBox label="Internal PN" value={currentPart.part_numbr} />
              <InfoBox label="Newer PN" value={currentPart.newer} />
              <InfoBox label="Part Category" value={currentPart.part_type} />
              <InfoBox label="Design Code" value={currentPart.equip_dsgn} />
              <div className="col-span-2">
                <InfoBox label="Compatible Models" value={currentPart.models} isTextarea />
              </div>
              <div className="col-span-2">
                <InfoBox label="Description 2" value={currentPart.descrip2} />
              </div>
            </div>
          </section>

          {/* Technical Specs */}
          {renderSpecs().length > 0 && (
          <section>
            <h3 className="text-xs font-black text-amber-600 dark:text-amber-500 uppercase tracking-widest mb-4 flex items-center gap-2">
              <SafeIcon icon={FiSettings} /> Technical Specs
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {renderSpecs()}
            </div>
          </section>
          )}

          {/* Intelligence Section */}
          {[currentPart.advice, currentPart.attn, currentPart.location, currentPart.source].some((v) => v && String(v).trim()) && (
          <section>
            <h3 className="text-xs font-black text-slate-500 dark:text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
              <SafeIcon icon={FiFileText} /> Intelligence
            </h3>
            <div className="space-y-4">
              <InfoBox label="Advice" value={currentPart.advice} isTextarea />
              <InfoBox label="Attention" value={currentPart.attn} isTextarea />
              <div className="grid grid-cols-2 gap-4">
                <InfoBox label="Location" value={currentPart.location} />
                <InfoBox label="Source" value={currentPart.source} />
              </div>
            </div>
          </section>
          )}
        </div>
      </div>

      {showFlagModal && (
        <FlagNLAModal 
          part={currentPart} 
          onClose={() => setShowFlagModal(false)} 
          onSuccess={() => {
            setShowFlagModal(false);
            onClose();
          }} 
        />
      )}
    </div>
  );
};

const InfoBox = ({ label, value, isTextarea }) => {
  if (!value || value.trim() === '') return null;
  return (
    <div className={`text-left ${isTextarea ? 'bg-slate-50 dark:bg-slate-950/50 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm' : ''}`}>
      {label && <div className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1">{label}</div>}
      <div className={`${isTextarea ? 'text-sm' : 'text-base'} text-slate-800 dark:text-slate-200 font-bold leading-relaxed`}>
        {value}
      </div>
    </div>
  );
};

export default PartDetailPanel;